import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { money } from '../../lib/format.js'
import { useJobOverlay } from '../../lib/job-overlay.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { DialogBody, DialogFooter, DialogHeader, Modal } from '../Modal.js'
import { combineLabel, dayLabel, leadLabel } from '../repeat/rule.js'
import { JoinedPill, SlotDot } from '../repeat/ServiceTracks.js'
import { Button, Textarea } from '../ui.js'
import type { AgreementPage } from './AgreementGrid.js'
import { useInvalidateAgreement } from './AgreementPopups.js'
import { planByVisit, serviceName, siteTitle, visitKey, visitsOf, type PlanLine } from './agreement-view.js'

type Visit = { scheduleId: string; sequence: number }

/**
 * A visit on the agreement's timeline that has no job yet: what goes on its
 * job with it, a note for each visit carried into the job's brief, and Make
 * job — early, if its creation day has not come, so the office can book it
 * in or add to it before the technician sees it. Make and open goes straight
 * to the job.
 *
 * What goes together is the server's `plan` line for the visit, and Make
 * sends that line's `scheduleId`, `sequence` and `with` to `repeat.createJob`
 * unchanged, as Coming up does.
 */
export function VisitPopup({
  page,
  visit,
  canSchedule,
  onClose,
  onMade,
}: {
  page: AgreementPage
  visit: Visit | null
  /** `SCHEDULE_EDIT` on an agreement that is running: notes and Make job. */
  canSchedule: boolean
  onClose: () => void
  onMade: (text: string) => void
}) {
  return (
    <Modal open={visit !== null} onClose={onClose} labelledBy="visit-popup-title" className="max-w-[560px]">
      {visit ? <VisitForm page={page} visit={visit} canSchedule={canSchedule} onClose={onClose} onMade={onMade} /> : null}
    </Modal>
  )
}

function VisitForm({
  page,
  visit,
  canSchedule,
  onClose,
  onMade,
}: {
  page: AgreementPage
  visit: Visit
  canSchedule: boolean
  onClose: () => void
  onMade: (text: string) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const overlay = useJobOverlay()
  const invalidate = useInvalidateAgreement()

  const line: PlanLine | null = planByVisit(page).get(visitKey(visit)) ?? null
  const members = (line ? visitsOf(line) : [visit]).flatMap((member) => {
    const row = page.schedules.find((schedule) => schedule.id === member.scheduleId)
    const slot = row?.slots.find((candidate) => candidate.sequence === member.sequence)
    return row && slot ? [{ ...member, row, slot, key: visitKey(member) }] : []
  })
  const [notes, setNotes] = useState<Record<string, string>>(() =>
    Object.fromEntries(members.map((member) => [member.key, member.slot.note ?? ''])),
  )
  const [opening, setOpening] = useState(false)

  const saveNote = useMutation(trpc.repeat.setVisitNote.mutationOptions())
  const create = useMutation(trpc.repeat.createJob.mutationOptions())
  const dirty = members.filter((member) => (notes[member.key] ?? '').trim() !== (member.slot.note ?? ''))
  const busy = saveNote.isPending || create.isPending
  const failure = saveNote.error ?? create.error

  const first = members[0]
  if (!first) {
    return (
      <>
        <DialogHeader id="visit-popup-title" title="Visit" onClose={onClose} />
        <DialogBody>
          <p className="text-[13px] text-ink-600">That visit is no longer on the agreement.</p>
        </DialogBody>
      </>
    )
  }

  const site = siteTitle(first.row.site, page.client.id)
  const services = members.map((member) => serviceName(member.row))
  const on = line?.on ?? first.slot.dueOn
  const together = members.length > 1
  const rule = combineLabel(page)
  const making = canSchedule && line !== null && line.canMake

  const saveNotes = async () => {
    for (const member of dirty) {
      await saveNote.mutateAsync({
        scheduleId: member.scheduleId,
        sequence: member.sequence,
        note: (notes[member.key] ?? '').trim() || null,
      })
    }
  }
  const make = async (open: boolean) => {
    if (!line) return
    setOpening(open)
    try {
      await saveNotes()
      const made = await create.mutateAsync({ scheduleId: line.scheduleId, sequence: line.sequence, with: line.with })
      invalidate()
      void queryClient.invalidateQueries(trpc.jobs.pathFilter())
      void queryClient.invalidateQueries(trpc.schedule.pathFilter())
      onMade(`Made the job for ${services.join(' + ')} at ${site.name}.`)
      onClose()
      if (open) overlay.openJob(made.jobId)
    } catch {
      // The mutation's error is shown in the footer.
    } finally {
      setOpening(false)
    }
  }

  return (
    <>
      <DialogHeader
        id="visit-popup-title"
        title={services.join(' + ')}
        description={`${site.name} · due ${dayLabel(on)}`}
        onClose={onClose}
      />
      <DialogBody className="gap-4">
        {together ? (
          <div className="flex items-start gap-2.5 rounded-lg bg-blue-50 px-3 py-2.5 text-[12.5px] text-blue-900 ring-1 ring-blue-200 ring-inset">
            <span aria-hidden="true" className="mt-0.5">
              <JoinedPill>
                <SlotDot mark="planned" small look="dots" />
                <SlotDot mark="planned" small look="dots" />
              </JoinedPill>
            </span>
            <span>
              <span className="font-semibold">Made as one job.</span>{' '}
              {`These ${members.length} visits at ${site.name} are due ${rule ?? 'together'}, so they go on one job with every service and each one’s instructions.`}
            </span>
          </div>
        ) : null}

        <ul className="flex flex-col gap-3">
          {members.map((member) => {
            const price = member.row.services.reduce((sum, entry) => sum + (entry.priceIncTax ?? 0), 0)
            return (
              <li key={member.key} className="flex flex-col gap-1.5">
                <div className="flex items-baseline gap-2 text-[13px]">
                  <span className="min-w-0 flex-1 truncate font-semibold text-ink-900">{serviceName(member.row)}</span>
                  <span className="text-ink-500 tabular-nums">{`Due ${dayLabel(member.slot.dueOn)}`}</span>
                  <span className="w-20 text-right text-ink-700 tabular-nums">
                    {price ? money(price) : <span className="text-amber-700">No price</span>}
                  </span>
                </div>
                {canSchedule ? (
                  <Textarea
                    aria-label={`Instructions for ${serviceName(member.row)}`}
                    value={notes[member.key] ?? ''}
                    placeholder="Instructions for this visit, carried into the job’s brief"
                    onChange={(event) => setNotes((current) => ({ ...current, [member.key]: event.target.value }))}
                    rows={2}
                  />
                ) : member.slot.note ? (
                  <p className="text-[12.5px] whitespace-pre-wrap text-ink-700">{member.slot.note}</p>
                ) : null}
              </li>
            )
          })}
        </ul>

        <p className="text-[12.5px] text-ink-600">{statusSentence(page, line, first.slot.tentative)}</p>
      </DialogBody>
      <DialogFooter error={failure ? errorMessage(failure) : null}>
        <Button onClick={onClose}>Close</Button>
        {canSchedule && dirty.length > 0 ? (
          <Button loading={saveNote.isPending && !create.isPending} disabled={busy} onClick={() => void saveNotes().then(invalidate, () => {})}>
            {dirty.length === 1 ? 'Save note' : 'Save notes'}
          </Button>
        ) : null}
        {making ? (
          <>
            <Button
              icon={<Icon name="plus" className="size-3.5" />}
              loading={busy && !opening}
              disabled={busy}
              onClick={() => void make(false)}
            >
              {line.ready ? 'Make job' : 'Make job now'}
            </Button>
            <Button variant="primary" loading={busy && opening} disabled={busy} onClick={() => void make(true)}>
              Make and open
            </Button>
          </>
        ) : null}
      </DialogFooter>
    </>
  )
}

function statusSentence(page: AgreementPage, line: PlanLine | null, tentative: boolean): string {
  if (!line) return 'No job can be made for this visit.'
  if (line.refusal) return line.refusal
  if (!line.canMake) {
    return tentative
      ? 'Its date is counted from when the last job is completed, so it can be made once that job is.'
      : 'Its job can be made once the visit before it has a job.'
  }
  if (line.ready) return 'Its job is ready to be made.'
  const lead = leadLabel(page.leadDays)
  return page.autoCreate
    ? `Its job is created on ${dayLabel(line.createOn)}, ${lead} before it’s due. Make it now to book it early or add to it.`
    : `Its job can be made from ${dayLabel(line.createOn)}, ${lead} before it’s due. Make it now to book it early or add to it.`
}
